import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Request interceptor for authentication and debugging
api.interceptors.request.use(
  (config) => {
    console.log('Making API request:', config.method.toUpperCase(), config.url);
    
    // Add authorization header if token exists
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response error:', error.response.status, error.response.data);
      
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          // Skip retry for auth endpoints
          const authEndpoints = ['/auth/login', '/auth/register', '/auth/logout', '/auth/refresh', '/auth/me'];
          const isAuthEndpoint = authEndpoints.some(endpoint => originalRequest.url.includes(endpoint));
          
          if (isAuthEndpoint) {
            // Don't retry auth endpoints, just clear and redirect
            if (originalRequest.url.includes('/auth/me') || originalRequest.url.includes('/auth/logout')) {
              // These failing is expected when not authenticated
              return Promise.reject(error);
            }
          }
          
          // Try to refresh token only for non-auth endpoints
          if (!originalRequest._retry && !isAuthEndpoint) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem('refreshToken');
            
            if (refreshToken) {
              try {
                const response = await axios.post(`${API_URL}/auth/refresh`, { refreshToken });
                const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;
                localStorage.setItem('accessToken', accessToken);
                if (newRefreshToken) {
                  localStorage.setItem('refreshToken', newRefreshToken);
                }
                
                // Update the user in localStorage if provided
                if (response.data.user) {
                  localStorage.setItem('user', JSON.stringify(response.data.user));
                }
                
                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return api(originalRequest);
              } catch (refreshError) {
                console.error('Token refresh failed, clearing auth');
                // Clear auth immediately
                localStorage.clear();
                // Only redirect if we're not already on the home page
                if (window.location.pathname !== '/') {
                  window.location.replace('/');
                }
                return Promise.reject(refreshError);
              }
            } else {
              // No refresh token, clear auth and redirect immediately
              console.log('No refresh token available, clearing auth');
              localStorage.clear();
              // Only redirect if we're not already on the home page
              if (window.location.pathname !== '/') {
                window.location.replace('/');
              }
              return Promise.reject(error);
            }
          }
          break;
        case 429:
          console.error('Rate limit exceeded');
          break;
        case 500:
          console.error('Server error');
          break;
        default:
          console.error('API error:', error.response.data);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    return Promise.reject(error);
  }
);

// Simple in-memory cache for Claude responses
const responseCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (dataSummary, userQuestion) => {
  return JSON.stringify({ dataSummary, userQuestion });
};

export const askClaude = async (dataSummary, userQuestion) => {
  // Check cache first
  const cacheKey = getCacheKey(dataSummary, userQuestion);
  const cached = responseCache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    console.log('Returning cached response');
    return cached.response;
  }
  
  try {
    const response = await api.post('/chat/analyze', {
      dataSummary,
      userQuestion
    });
    
    // Cache the successful response
    responseCache.set(cacheKey, {
      response: response.data.response,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries
    if (responseCache.size > 50) {
      const oldestKey = responseCache.keys().next().value;
      responseCache.delete(oldestKey);
    }
    
    return response.data.response;
  } catch (error) {
    // Re-throw with a user-friendly message
    if (error.response?.status === 429) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    }
    if (error.response?.status === 401) {
      throw new Error('API authentication failed. Please check the API key configuration.');
    }
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to get AI response. Please try again.');
  }
};

// Health check function
export const checkHealth = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw new Error('Cannot connect to server');
  }
};

// Test Claude connection
export const testClaudeConnection = async () => {
  try {
    const response = await api.get('/chat/test');
    return response.data;
  } catch (error) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('Failed to test Claude connection');
  }
};

// Authentication API functions
export const authAPI = {
  // Register new user
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      // Store tokens and user info on successful registration
      const { tokens, user } = response.data;
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Registration failed. Please try again.');
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      // Store tokens and user info on successful login
      const { tokens, user } = response.data;
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Login failed. Please check your credentials.');
    }
  },

  // Logout user (client-side only)
  logout: () => {
    // Clear auth data from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('onboardingProgress');
    localStorage.removeItem('searchExecutionState');
    // Keep theme and other non-auth settings
  },

  // Change password
  changePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/change-password', passwordData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Password change failed. Please try again.');
    }
  },

  // Get current user info
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    try {
      const user = JSON.parse(userStr);
      // Validate that user has all required fields
      const requiredFields = ['id', 'email'];
      const hasAllRequiredFields = requiredFields.every(field => user.hasOwnProperty(field));
      
      // Check if user object has critical fields that were added later
      // Note: dashboardReleased might not exist for users who logged in before this field was added
      // So we only check for onboardingCompleted as critical
      const hasCriticalFields = user.hasOwnProperty('onboardingCompleted');
      
      if (!hasAllRequiredFields || !hasCriticalFields) {
        console.log('Invalid user data in localStorage, clearing auth data...');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        return null;
      }
      
      return user;
    } catch (error) {
      console.error('Failed to parse user data from localStorage', error);
      localStorage.clear();
      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    return !!(token && user);
  },

  // Validate current token
  validateToken: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

// User management API functions
export const userAPI = {
  // Get user profile
  getProfile: async () => {
    try {
      const response = await api.get('/users/profile');
      return response.data.user;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get user profile.');
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/users/profile', userData);
      // Update stored user info
      const currentUser = authAPI.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...response.data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update profile.');
    }
  },

  // Get user dashboard data
  getDashboard: async () => {
    try {
      const response = await api.get('/users/dashboard');
      return response.data.dashboard;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get dashboard data.');
    }
  },

  // Get comprehensive overview dashboard data across all executions
  getOverviewDashboard: async () => {
    try {
      const response = await api.get('/users/overview-dashboard');
      return response.data.overview;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get overview dashboard data.');
    }
  },

  // Get user analyses
  getAnalyses: async (options = {}) => {
    try {
      const params = new URLSearchParams();
      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.sortOrder) params.append('sortOrder', options.sortOrder);
      
      const response = await api.get(`/users/analyses?${params.toString()}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get analyses.');
    }
  },

  // Save analysis
  saveAnalysis: async (analysisData) => {
    try {
      const response = await api.post('/users/analyses', analysisData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to save analysis.');
    }
  },

  // Get user settings
  getSettings: async () => {
    try {
      const response = await api.get('/users/settings');
      return response.data.settings;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get settings.');
    }
  },

  // Update user settings
  updateSettings: async (settings) => {
    try {
      const response = await api.put('/users/settings', settings);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to update settings.');
    }
  },
};

// Query Templates API functions
export const queryTemplatesAPI = {
  // Get template by ID
  getTemplateById: async (templateId) => {
    try {
      const response = await api.get(`/query-templates/${templateId}`);
      return response.data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to get template details.');
    }
  },
};

export default api;